import { lazy, type ComponentType, type ReactNode } from "react";
import { createBrowserRouter, Navigate, type RouteObject } from "react-router";
import { AppLayout } from "@/app/layouts/AppLayout";
import { LegacyRedirect } from "@/app/routes/LegacyRedirect";
import { ProtectedRoute } from "@/app/routes/ProtectedRoute";
import { RouteBoundary } from "@/app/routes/RouteBoundary";
import RouteErrorPage from "@/app/routes/RouteErrorPage";
import { NAV_CATALOG, type Installation, type NavPanel } from "@/app/navigation/nav-catalog";
import type { LegacyParamRule } from "@/hooks/useLegacySearchParams";

/**
 * Flattened ONCE, at module scope. `navItemFor` walks this list on every navigation, and
 * flattening fifteen groups per keystroke is work with no reader.
 *
 * NOT the kit's `flattenNav`: that one carries the panel and drops `aboutOthers`, and the
 * route gate needs it. Whether a path is about other people decides which permission check
 * runs — `can` or `canForOthers` — so losing it here would let a SELF-scoped key open a screen
 * listing everybody. The group answers; the item inherits.
 */
const NAV_ITEMS = NAV_CATALOG.flatMap((group) =>
  group.items.map((item) => ({
    ...item,
    panel: group.panel,
    /* `ownRecords` satırı kişinin kendi kayıtları: grup «başkalarının» olsa da SELF anahtar yeter. */
    aboutOthers: item.ownRecords ? false : group.aboutOthers,
    /**
     * Carried for the same reason `aboutOthers` is. The route table is built from the FULL
     * catalogue — it has no session at module scope and therefore cannot know which shape of
     * installation this is — so the answer travels with the item and `ProtectedRoute` asks the
     * session at render time. Dropping it here would leave `/platform/tenants` openable by URL
     * on a dedicated installation, which is precisely the shape of the menu-fixed-router-not
     * bug that `requireAnyRecord` already exists for.
     */
    ...(group.installation ? { installation: group.installation } : {}),
  })),
);

/**
 * The route table, DERIVED from the navigation catalogue.
 *
 * WHY DERIVED AND NOT HAND-WRITTEN. A hand-written table and a hand-written menu drift: a
 * screen gets a permission gate in the menu and a different one in the router, and the gap is
 * only found when somebody with an unusual role sees something they should not. Generating the
 * routes from the same rows the menu is filtered from removes that class of bug entirely —
 * there is no second place to forget.
 *
 * `SCREENS` maps a path to its page. A catalogue entry with no page yet renders the
 * "planned screen" notice instead of 404, because the menu item is real and the permission
 * check passed; only the page is missing. Registering the page here deletes the placeholder.
 *
 * `EXTRA_ROUTES` carries what the menu cannot: detail and wizard paths (`.../:id`,
 * `.../yeni`). They inherit their parent item's gate through `gateFor`.
 */

const lazyPage = (loader: () => Promise<{ default: ComponentType }>) => lazy(loader);

const PlannedScreenPage = lazyPage(() => import("@/features/system/pages/PlannedScreenPage"));
/** Notlarım: liste ve belge TEK ekran — iki rota da bu nesneyi çiziyor (aşağıda `/notes/:noteId`). */
const NotesWorkspacePage = lazyPage(() => import("@/features/notes/pages/NotesWorkspacePage"));
/**
 * PERSONEL BÖLÜMÜ — dört sekme adresi, TEK tembel nesne.
 *
 * Her sekme için ayrı bir `lazyPage(...)` yazmak dört ayrı bileşen TİPİ üretir; React sekme
 * değişiminde bölümü söküp yeniden kurar — açık süzgeç, kaydırma ve tablo durumu gider, dört
 * istek yeniden atılır. Notlar rayında aynı sebeple aynı çözüm kullanılıyor.
 */
const PersonnelSectionPage = lazyPage(
  () => import("@/features/employees/pages/PersonnelSectionPage"),
);
/**
 * «İzinlerim» — dört adres (`/my-leave` ve üç sekmesi), TEK tembel nesne; gerekçesi
 * `PersonnelSectionPage` ile aynı: ayrı `lazyPage(...)` çağrıları ayrı bileşen tipi olur ve
 * sekme değişiminde bölüm sökülüp yeniden kurulur (izin-sade, 20 Ekim 2026).
 */
const MyLeaveSectionPage = lazyPage(() => import("@/features/leave/pages/MyLeaveSectionPage"));
/**
 * «Ekip geri bildirimleri» — iki adres (`/team/feedback` yazan ve zincirdeki okuyucu için, `/hr/team-feedback` İK'nın
 * şirket geneli için), TEK tembel nesne; gerekçesi `PersonnelSectionPage` ile aynı (rol-yetki 21 Ekim 2026).
 */
const TeamFeedbackPage = lazyPage(() => import("@/features/team/pages/TeamFeedbackPage"));
/** «Kanal» bölümü — dört sekme, tek tembel nesne; gerekçesi `PersonnelSectionPage` ile aynı. */
const ChannelSectionPage = lazyPage(() => import("@/features/channel/pages/ChannelSectionPage"));
/** «Analiz» bölümü (A2, 1 Kasım 2026) — genel bakış ve dört alan sekmesi, tek tembel nesne; aynı gerekçe. */
const AnalyticsSectionPage = lazyPage(() => import("@/features/analytics/pages/AnalyticsSectionPage"));
/**
 * Stok belgesi sihirbazı — `/inventory/documents/new` ve `/:id/edit` TEK tembel nesne (17 Ekim
 * 2026): yeni taslak kaydedilince adres ikincisine geçiyor ve aynı bileşen tipi kaldığı için
 * sayfa sökülüp yeniden kurulmuyor — kişinin bulunduğu adım ve yazdıkları yerinde duruyor.
 */
const DocumentWizardPage = lazyPage(() => import("@/features/inventory/pages/DocumentWizardPage"));
const NotFoundPage = lazyPage(() => import("@/features/system/pages/NotFoundPage"));
const LoginPage = lazyPage(() => import("@/features/auth/pages/LoginPage"));
const AcceptInvitePage = lazyPage(() => import("@/features/auth/pages/AcceptInvitePage"));
const ResetPasswordPage = lazyPage(() => import("@/features/auth/pages/ResetPasswordPage"));
/**
 * HATA SAYFASI TEMBEL DEĞİL (9 Ekim 2026, 5. tur). En sık üretim hatası, yeni bir dağıtımdan
 * sonra eski sekmenin artık sunucuda olmayan bir parçayı istemesi — ve o anda hata sayfasının
 * KENDİ parçası da aynı sebeple yüklenemez; React Router o zaman kendi İngilizce ekranına düşer.
 * Sayfa küçük ve bağımlılıkları (düğme, simge, hata sözlüğü) ana pakette zaten var.
 */
const publicRouteError = <RouteErrorPage />;
/**
 * Bileşen galerisi — YALNIZ GELİŞTİRME DERLEMESİNDE.
 *
 * Ölçüldü: galeri üretim paketinde 783 kB (gzip 297 kB) ve bölüm parçalarıyla birlikte ~1,1 MB
 * — React'in kendisinin üç katı. Tembel yüklendiği için ilk boyamayı yavaşlatmıyor ama her
 * dağıtımda üretiliyor, taşınıyor ve sunuluyor; müşterinin kurulumunda hiçbir işe yaramadan.
 *
 * ERİŞİMİ DE OTURUM İSTEMİYOR ve bu geliştirmede kasıtlıydı: kimse renk bakmak için giriş
 * yapmasın diye. Üretimde ise aynı karar, İntraline'ın kurulumunda bütün bileşen kataloğunu
 * anonim erişime açık bırakmak anlamına geliyor.
 *
 * `import.meta.env.DEV` bir çalışma zamanı kontrolü DEĞİL: Vite bu dalı üretimde tamamen
 * eliyor, yani `import()` hiç yazılmıyor ve parça hiç üretilmiyor. Bir bayrakla gizlemek,
 * dosyayı yine de göndermek olurdu.
 */
const StyleGuidePage = import.meta.env.DEV
  ? lazyPage(() => import("@altek/ui/catalog/pages/StyleGuidePage"))
  : null;

/**
 * Implemented screens, by path. Everything else in the catalogue falls back to the notice.
 * Each entry is its own chunk: a route nobody visits should not be in the download.
 */
/**
 * Dışa açık, YALNIZ sınama için: bir ekranın burada olması onu erişilebilir YAPMIYOR. Rotalar
 * katalogdan üretiliyor, yani menüden düşen bir adres sessizce rotadan da düşüyor — bu dosyada
 * yirmi altı satır tam olarak öyle ölmüştü. `router.test.ts` ikisini karşılaştırıyor.
 */
export const SCREENS: Record<string, ComponentType> = {
  // Personal workspace — every one of these is about the signed-in account, so they are
  // reachable from every rail including an administrator's.
  "/chat": lazyPage(() => import("@/features/chat/pages/ChatPage")),
  "/directory": lazyPage(() => import("@/features/directory/pages/DirectoryPage")),
  "/profile": lazyPage(() => import("@/features/directory/pages/ProfilePage")),
  "/notes": NotesWorkspacePage,
  "/diagrams": lazyPage(() => import("@/features/diagrams/pages/DiagramsPage")),
  "/converter": lazyPage(() => import("@/features/converter/pages/ConverterPage")),
  "/documents": lazyPage(() => import("@/features/documents/pages/DocumentsPage")),

  /**
   * PANELİM — ürünün TEK panosu, ve katalogdaki tek pano satırı.
   *
   * «İK Panosu» (`/hr`) ve «Yönetim Panosu» (`/admin`) 16 Eylül 2026'da kaldırıldı (kullanıcı
   * kararı): ikisi de departman panosuydu ve içerikleri bu ekranın içinde birer BLOĞA dönüştü.
   * `HrDashboardPage` ve `AdminDashboardPage` silindi.
   *
   * KENDİ SELF-SERVİS EKRANLARI ARTIK BURADA DEĞİL: dördü de menüden çıktı ve `EXTRA_ROUTES`'a
   * KENDİ anahtarlarıyla taşındı. `SCREENS`'te kalsalardı iki yerde birden tanımlı olurlardı.
   */
  "/my-panel": lazyPage(() => import("@/features/dashboard/pages/EmployeeDashboardPage")),
  /**
   * Bildirim bir HESABA aittir, bir çalışana değil — çalışan kaydı olmayan bir yöneticiye de
   * ulaşır. O yüzden bu ikisi izin ekranlarının aksine istihdam istemez.
   *
   * «Duyurular» YÖNETİM listesini değil `/announcements/active`'i okur: yönetim listesi
   * `manage` anahtarı ister ve taslakları da döndürür. Tek uç iki işi görseydi, herkesin
   * okuduğu yüzey yayınlanmamış taslakları sızdırırdı.
   */
  "/hr/announcements": lazyPage(
    () => import("@/features/notifications/pages/AnnouncementAdminPage"),
  ),
  /**
   * EKİBİM VE EKİP GERİ BİLDİRİMLERİ (rol-yetki 21 Ekim 2026). `/team`: bir yöneticinin kendi ekibi (`hr.team.read`).
   * `/hr/team-feedback`: İK'nın şirket geneli okuması (`hr.team_feedback.read_all`) — menü satırı. Yazan ve zincirdeki
   * okuyucu `/team/feedback`'i `EXTRA_ROUTES`'tan açar (kendi anahtarıyla); üç adres, ikisi AYNI sayfa nesnesi.
   */
  "/team": lazyPage(() => import("@/features/team/pages/MyTeamPage")),
  "/hr/team-feedback": TeamFeedbackPage,
  "/hr/leave-requests": lazyPage(() => import("@/features/leave/pages/HrLeaveRequestsPage")),
  "/hr/employees": lazyPage(() => import("@/features/employees/pages/EmployeesPage")),
  // `/hr/employments` SİLİNDİ — ik-personel 6 Ekim 2026 (H5 KARAR «istihdam kayıtları
  // kaldırılacak»): ekran yok, adres `LEGACY_HR_REDIRECTS` ile Personel › Çalışanlar'a gidiyor.
  /**
   * İzin bakiyesi ve defteri LİSTE EKRANI DEĞİL: iki uç da `employmentId` zorunlu kılıyor,
   * çünkü bakiye şirketin sayfalayabileceği bir tablo değil, bir sözleşmeye ait olgudur.
   * Seçim URL'de (`?employment=`) durur ve iki ekran arasında korunur.
   */
  "/hr/leave-balances": lazyPage(() => import("@/features/leave/pages/LeaveBalancesPage")),
  "/hr/leave-ledger": lazyPage(() => import("@/features/leave/pages/LeaveLedgerPage")),
  "/hr/documents": lazyPage(() => import("@/features/documents-hr/pages/HrDocumentsPage")),
  /**
   * «Belgelerim» — kişinin KENDİ özlük dosyası, «Şirket» bölümünün üçüncü satırı.
   *
   * SAYFA VARDI, KAYIT YOKTU. 15 Eylül'de satır «Benim işlerim»den «Şirket»e taşınırken bu
   * kayıt düştü ve kimse fark etmedi — çünkü `SCREENS`'te karşılığı olmayan bir menü satırı
   * hata vermiyor, `PlannedScreenPage` («bu ekran henüz yapılmadı») çiziyor. Yani menüde duran
   * satır, hazır bir sayfanın yerine «yapılmadı» yazan bir yer tutucuya götürüyordu. 16 Eylül'de
   * `check:screen-access` ölçtü.
   */
  /**
   * Ayrı bir ekran DEĞİL — aynı ekranın filtrelenmiş hâli, sarmalayıcı bir dosyayla.
   *
   * `SCREENS` kasten prop almaz: rota tablosu menü kataloğundan türetiliyor ve her satırın
   * "bu yol şu ekrandır" dışında bir şey söylememesi, menüyle router'ın ayrışmamasını sağlayan
   * şeydir. Filtreyi ekranın `useLocation()` ile kendisinin okuması da elendi: ekranı bir yola
   * bağlar, yol değişince ekran sessizce filtresiz çalışır ve hiçbir şey bunu söylemez.
   */
  "/hr/expiring": lazyPage(() => import("@/features/documents-hr/pages/HrExpiringDocumentsPage")),
  /**
   * İzin YAPILANDIRMASI: tür → politika → takvim. Üçü bir zincir ve okuma sırası bu.
   *
   * Tür MÜŞTERİYE aittir (sunucunun liste sorgusunda `legalEntityId` yok), politika ŞİRKETE.
   * «Yıllık İzin» bütün şirketlerde aynı şeyi anlatır; kaç gün ettiği her şirketin kararıdır.
   */
  "/hr/leave-types": lazyPage(() => import("@/features/leave/pages/LeaveTypesPage")),
  "/hr/leave-policies": lazyPage(() => import("@/features/leave/pages/LeavePoliciesPage")),
  // ik-izin 6 Ekim 2026: `/hr/work-calendars` ekranı kalktı (KARAR «çalışma takvimi kalkacak»).
  // Takvimi her şirkete `seed:hr-catalogs` kuruyor; eski adres «Politika» sekmesine gidiyor.
  /**
   * Liste değil TAKVİM: liste «hangi talep kararımı bekliyor», ay ızgarası «bunu onaylarsam
   * perşembe boşta kalır mı» sorusunu yanıtlar. İkisi aynı veri, iki farklı soru.
   */
  "/hr/leave-calendar": lazyPage(() => import("@/features/leave/pages/LeaveCalendarPage")),
  "/hr/document-types": lazyPage(() => import("@/features/documents-hr/pages/DocumentTypesPage")),
  /**
   * ÖZEL NİTELİKLİ VERİ. Teşhis ve hekim notu hiç saklanmaz; rapor numarası maskelidir ve
   * görüntülenmesi ayrı yetki, zorunlu gerekçe ve kalıcı erişim kaydı ister.
   */
  "/hr/medical-reports": lazyPage(() => import("@/features/medical/pages/MedicalReportsPage")),
  /**
   * Kuruluş › Birimler'den ayrı: orası «hangi birimler var» tablosu, burası «şirket nasıl kurulmuş» şekli.
   * 21 Ekim 2026'dan beri şema DÜZENLENİYOR (`organization.org_unit.manage` olan için: birim ekle/taşı/pasife al,
   * birim başı, kadro); olmayan için salt okunur. Kuruluş › Birimler aynı uçlarla yerinde duruyor.
   */
  "/hr/org-chart": lazyPage(() => import("@/features/organization/pages/OrgChartPage")),
  /**
   * AYNI EKRAN, İKİ ADRES — ve ikisi de gerekli.
   *
   * `/hr/org-chart` İK bölümünün sekmesi: şemayı düzenleyen, birim açan kişinin yolu.
   * `/company/org-chart` «Şirket» bölümünün satırı: şemaya BAKAN herkesin yolu. Kapıları
   * farklı (biri İK anahtarları, öbürü `organization.org_unit.read`), yani tek adrese
   * indirmek ikisinden birini yanlış kapıya bağlamak olurdu.
   */
  /**
   * PUANTAJ. Bir günün hesabı sunucudaki motorun cevabı; bu ekran gün OLUŞTURMAZ ve
   * DÜZENLEMEZ — bakılacakları gösterir ve kapatır. Değiştirmek isteyen damgayı değiştirir,
   * gün kendiliğinden düzelir.
   *
   * Kural tek satır ve üç yerde birden korunuyor (motor, yirmi dört birim testi, veritabanı
   * CHECK kısıtı): beklenen = takvimin günü − o güne düşen onaylı izin. İzinli bir gün
   * devamsızlık değildir ve YARIM gün yarımdır.
   */
  // PUANTAJ KAPATILDI, 16 Eylül 2026 (kullanıcı kararı; gerekçe `LeaveSectionPage.tsx`'te).
  // Ekran ve modülü duruyor; PDKS bağlandığı gün bu satır ve aşağıdaki `/my-attendance`
  // yorumdan çıkar, `LeaveSectionPage`'teki blok geri açılır.
  // "/hr/attendance": lazyPage(() => import("@/features/attendance/pages/AttendancePage")),
  /**
   * YÖNETİM BÖLÜMLERİ. Otuz menü satırı dört bölüme indi; her bölüm sekmeli bir sayfa ve her
   * sekme kendi adresini taşıyor (`/admin/system/data-model`), çünkü sekme de "neye bakıyorum"
   * sorusunun parçası ve paylaşılabilir olmalı.
   *
   * MENÜ BÖLÜME İŞARET EDİYOR, sekmeye değil — sekme zaten sayfanın içinde görünüyor ve onu
   * bir de kenar çubuğunda listelemek aynı gezinmeyi iki yerde öğretmekti. Bu yüzden katalogdan
   * üretilen rota BÖLÜM KÖKÜ (`/admin/organization`); sekme adresleri `EXTRA_ROUTES` üzerinden aynı
   * köke bağlanıyor, yani yetki kapısı ortak ve yer imleri kırılmadı. Slug'sız bir kök adreste
   * `AdminSection` ilk yetkili sekmeye düşüyor, bu yüzden `:tab` desenine gerek yok.
   *
   * ESKİ ADRESLER SİLİNMEDİ. `/admin/users`, `/admin/roles` ve diğer yirmi yedisi olduğu gibi
   * çalışmaya devam ediyor: biri yer imine eklemiş, biri bir belgeye yazmış olabilir. Menü
   * artık bölümlere işaret ediyor, adresler ise kimseyi kırmıyor.
   */
  /**
   * STOK BÖLÜMÜ. On dokuz menü satırı beş sekmeye indi; menü artık bölüme işaret ediyor.
   * Eski on altı adres `LEGACY_INVENTORY_REDIRECTS` ile sekmelerine bağlandı — yer imleri
   * kırılmasın diye, ve `router.test.ts` her `SCREENS` kaydının bir rotası olmasını şart
   * koşuyor.
   */
  "/inventory": lazyPage(() => import("@/features/inventory/pages/InventorySectionPage")),
  /** İK BÖLÜMLERİ. On yedi menü satırı beşe indi; üç bölüm sayfası, sekmeler içeride. */
  "/hr/people": PersonnelSectionPage,
  "/hr/leave": lazyPage(() => import("@/features/leave/pages/LeaveSectionPage")),
  // «Özlük Dosyası» (`/hr/personnel-files`) SİLİNDİ — ik-personel 6 Ekim 2026 (H9): içi
  // Personel › Belgeler sekmesinde, sağlık raporları İzin Yönetimi › Raporlar'da; eski adresler
  // `LEGACY_HR_REDIRECTS` ile.
  /**
   * ETKİNLİK. `/events/plans` ve `/physicians` menüde kendi satırları; `/events/:id`
   * `EXTRA_ROUTES`'ta, çünkü menüde karşılığı olmayan bir detay sayfası.
   */
  "/events": lazyPage(() => import("@/features/events/pages/EventsPage")),
  "/events/plans": lazyPage(() => import("@/features/events/pages/EventPlansPage")),
  "/physicians": lazyPage(() => import("@/features/events/pages/PhysiciansPage")),
  "/physician-visits": lazyPage(
    () => import("@/features/physician-visits/pages/PhysicianVisitsPage"),
  ),
  /**
   * PAZARLAMA TALEPLERİ — pz-akis 6 Ekim 2026 (plan M12, M15). İkisi de pazarlamanın
   * listesi; temsilcinin kendi sunum talepleri ve ürün talebinin kartı `EXTRA_ROUTES`'ta,
   * kendi kapılarıyla.
   */
  "/presentation-requests": lazyPage(
    () => import("@/features/presentation-requests/pages/PresentationRequestsPage"),
  ),
  /* 30 Ekim 2026: kişisel sunum talepleri artık menüde — kapısı katalog satırından geliyor. */
  "/my-presentation-requests": lazyPage(
    () => import("@/features/presentation-requests/pages/MyPresentationRequestsPage"),
  ),
  "/product-requests": lazyPage(
    () => import("@/features/product-requests/pages/ProductRequestsPage"),
  ),
  /** FİLO (6 Ekim 2026, `module.fleet`). Altı menü satırı; araç kartı ve sihirbazlar `EXTRA_ROUTES`'ta. */
  "/fleet": lazyPage(() => import("@/features/fleet/pages/FleetOverviewPage")),
  "/fleet/vehicles": lazyPage(() => import("@/features/fleet/pages/FleetVehiclesPage")),
  "/fleet/assignments": lazyPage(() => import("@/features/fleet/pages/FleetAssignmentsPage")),
  "/fleet/maintenance": lazyPage(() => import("@/features/fleet/pages/FleetMaintenancePage")),
  "/fleet/fines": lazyPage(() => import("@/features/fleet/pages/FleetFinesPage")),
  "/fleet/costs": lazyPage(() => import("@/features/fleet/pages/FleetCostsPage")),
  "/hr/assets": lazyPage(() => import("@/features/assets/pages/AssetsPage")),
  /** 30 Ekim 2026: çalışanların hasar ve değişim talepleri — kapısı menü satırından (`hr.asset_request.read`). */
  "/hr/asset-requests": lazyPage(() => import("@/features/asset-requests/pages/AssetRequestsPage")),
  /** 31 Ekim 2026: çıkış hesapları — kapısı menü satırından (`hr.exit_settlement.read`). */
  "/hr/exit-settlements": lazyPage(() => import("@/features/exit-settlements/pages/ExitSettlementsPage")),
  /**
   * `matchPrefix` ile menüde tek satır; sekmeler `/hr/training/participation` ve
   * `/hr/training/courses` olarak URL'de yaşıyor — `AdminSection`'ın kuralı.
   */
  "/hr/training": lazyPage(() => import("@/features/training/pages/TrainingSectionPage")),
  "/admin/organization": lazyPage(() => import("@/features/admin/pages/OrganizationSectionPage")),
  "/admin/access": lazyPage(() => import("@/features/admin/pages/AccessSectionPage")),
  "/admin/system": lazyPage(() => import("@/features/admin/pages/SystemSectionPage")),
  "/admin/server": lazyPage(() => import("@/features/admin/pages/ServerStatusPage")),
  "/admin/audit": lazyPage(() => import("@/features/admin/pages/AuditSectionPage")),
  "/admin/users": lazyPage(() => import("@/features/admin/pages/UsersPage")),
  "/admin/roles": lazyPage(() => import("@/features/admin/pages/RolesPage")),
  "/admin/permission-matrix": lazyPage(() => import("@/features/admin/pages/PermissionMatrixPage")),
  "/admin/modules": lazyPage(() => import("@/features/admin/pages/ModulesPage")),
  "/admin/departments": lazyPage(() => import("@/features/admin/pages/DepartmentsPage")),
  "/admin/legal-entities": lazyPage(() => import("@/features/admin/pages/LegalEntitiesPage")),
  "/admin/job-titles": lazyPage(() => import("@/features/admin/pages/JobTitlesPage")),
  "/admin/cost-centers": lazyPage(() => import("@/features/admin/pages/CostCentersPage")),
  "/admin/org-units": lazyPage(() => import("@/features/admin/pages/OrgUnitsPage")),
  "/admin/sites": lazyPage(() => import("@/features/admin/pages/SitesPage")),
  "/admin/positions": lazyPage(() => import("@/features/admin/pages/PositionsPage")),
  "/admin/custom-fields": lazyPage(() => import("@/features/admin/pages/CustomFieldsPage")),
  "/admin/field-policies": lazyPage(() => import("@/features/admin/pages/FieldPoliciesPage")),
  "/admin/lookups": lazyPage(() => import("@/features/admin/pages/LookupTypesPage")),
  "/admin/sector-profiles": lazyPage(() => import("@/features/admin/pages/SectorProfilesPage")),
  "/admin/settings": lazyPage(() => import("@/features/admin/pages/SettingsPage")),
  "/admin/storage-targets": lazyPage(() => import("@/features/admin/pages/StorageTargetsPage")),
  "/admin/notification-templates": lazyPage(
    () => import("@/features/admin/pages/NotificationTemplatesPage"),
  ),
  "/admin/workflows": lazyPage(() => import("@/features/admin/pages/WorkflowDefinitionsPage")),
  "/admin/jobs": lazyPage(() => import("@/features/admin/pages/JobsPage")),
  "/admin/sessions": lazyPage(() => import("@/features/admin/pages/SessionsPage")),
  "/admin/delegations": lazyPage(() => import("@/features/admin/pages/DelegationsPage")),
  "/admin/audit-logs": lazyPage(() => import("@/features/admin/pages/AuditLogsPage")),
  "/admin/sensitive-access": lazyPage(() => import("@/features/admin/pages/SensitiveAccessPage")),
  "/admin/security-events": lazyPage(() => import("@/features/admin/pages/SecurityEventsPage")),
  "/admin/integrity": lazyPage(() => import("@/features/admin/pages/IntegrityFindingsPage")),
  // Stok. The module's own panel; each screen is gated on `module.inventory` in the catalogue.
  "/inventory/products": lazyPage(() => import("@/features/inventory/pages/ProductsPage")),
  "/inventory/warehouses": lazyPage(() => import("@/features/inventory/pages/WarehousesPage")),
  "/inventory/documents": lazyPage(() => import("@/features/inventory/pages/DocumentsPage")),
  "/inventory/lots": lazyPage(() => import("@/features/inventory/pages/LotsPage")),
  "/inventory/counts": lazyPage(() => import("@/features/inventory/pages/CountsPage")),
  "/inventory/balances": lazyPage(() => import("@/features/inventory/pages/BalancesPage")),
  /**
   * Defter EKLENİR, düzeltilmez: bakiye bu satırların toplamıdır, ayrıca tutulan bir sayı
   * değil. Bu yüzden imleçle sayfalanır — tablo okunurken yazılıyor, OFFSET satırları ya
   * tekrarlar ya atlar ve «sayfa 40 / 812» çizildiği anda yanlıştır.
   */
  "/inventory/ledger": lazyPage(() => import("@/features/inventory/pages/LedgerPage")),
  /** Rafta duran ama başkasına ayrılmış stok: «var» ile «verilebilir» arasındaki fark. */
  "/inventory/reservations": lazyPage(() => import("@/features/inventory/pages/ReservationsPage")),
  "/inventory/reorder-rules": lazyPage(() => import("@/features/inventory/pages/ReorderRulesPage")),
  /**
   * MEVZUAT YÜZEYİ, depo yüzeyi değil. Gönderilememiş bir İTS bildirimi hiçbir stok
   * rakamında görünmeyen bir hukuki açıktır: mal çıkmıştır, bakiye doğrudur, kurum
   * haberdar değildir.
   */
  "/inventory/its-notifications": lazyPage(() => import("@/features/inventory/pages/ItsQueuePage")),
  /**
   * SALT OKUR. Bir kutuyu nerede olduğunu görmek için okutmak, onu hareket ettiren şey
   * olmamalı — ekran sözleşmesinin HARD RULE'u. Altında sunulan her eylem ayrı bir yazma ve
   * ayrı bir yetkinin arkasında.
   */
  "/inventory/lookup": lazyPage(() => import("@/features/inventory/pages/Gs1LookupPage")),
  /**
   * ÖLÇÜ KATALOĞU. Deponun kendi sözlüğü: `CLAUDE.md` «bir miktar her zaman birimiyle görünür»
   * diyor ve bugüne kadar o birimi tanımlayan bir ekran yoktu. Dönüşüm ÜRÜNE özeldir —
   * kilogramı grama çevirmek aritmetik, koliyi adede çevirmek ürünün kendi bilgisidir.
   */
  /**
   * MALİ TEMEL. Bir teklif satırının soracağı üç sorunun cevabı: fiyat hangi listeden, vergi
   * hangi oranla, dövizli tutar hangi kurla. Üçü de satışın altında değil kendi panelinde,
   * çünkü alış modülü geldiğinde aynı tabloları okuyacak.
   */
  /**
   * SATIŞ ZİNCİRİ GERİ (17 Ekim 2026, kullanıcı talimatı: «eksik ekranları da yapalım, ekleyelim,
   * çalıştığından emin olalım»).
   *
   * 21 Eylül'de teklif, sipariş, fiyat listesi, döviz kuru ve kullanılabilirlik satırları menüden
   * ve buradan çıkarılmıştı (`b7f42d6c`: «notlarda olmayan ve kaydı 0 olan satırlar»). Kod
   * yerinde kalmıştı; şimdi rotaları geri, ve kalkarken bilinen arızaları kapatılmış hâlde:
   * birimsiz miktar, hiç çalışmamış «Onayla ve rezerve et», satır ekleyemeyen satış müdürlüğü.
   * Aday, fırsat ve aktivite (CRM hunisi) geri GELMEDİ — talimat satış zincirini sayıyordu.
   *
   * Teklif ne söylediğimizin, sipariş ne üzerinde anlaştığımızın kaydı; ikisi ayrı ekran çünkü
   * ayrı belgeler — biri kopyalanarak öbürünü doğuruyor, bağlanarak değil.
   */
  "/sales/quotations": lazyPage(() => import("@/features/sales/pages/QuotationsPage")),
  "/sales/orders": lazyPage(() => import("@/features/sales/pages/OrdersPage")),
  /** S-26 (satis-duzeltme 20 Ekim 2026): «İş Ortakları»nın alıcılara süzülmüş hâli, Satış menüsünde. */
  "/sales/customers": lazyPage(() => import("@/features/partners/pages/CustomersPage")),
  "/sales/channel": ChannelSectionPage,
  /** ANALİZ (A2, 1 Kasım 2026): kapı menü satırından (`anyPermissions`, `aboutOthers`); sekmeler bölümün içinde. */
  "/analytics": AnalyticsSectionPage,
  "/finance/price-lists": lazyPage(() => import("@/features/finance/pages/PriceListsPage")),
  /** OPERASYON (17 Ekim 2026) — sevkiyat işleri ve kapı; kapıları menü satırlarından. */
  /** Sevkiyat takibi (1 Kasım 2026): kim, ne zaman yükledi, götürdü, teslim etti. */
  "/operations/shipment-tracking": lazyPage(
    () => import("@/features/operations/pages/ShipmentTrackingPage"),
  ),
  "/operations/transport-jobs": lazyPage(
    () => import("@/features/operations/pages/TransportJobsPage"),
  ),
  "/operations/gate-visits": lazyPage(() => import("@/features/operations/pages/GateVisitsPage")),
  /** Şoför hesabı (1 Kasım 2026): sürücü kartları ve hesap açma; şoförün kendi seferleri. */
  "/operations/drivers": lazyPage(() => import("@/features/operations/pages/DriversPage")),
  "/my-trips": lazyPage(() => import("@/features/operations/pages/MyTripsPage")),
  "/finance/tax-categories": lazyPage(() => import("@/features/finance/pages/TaxCategoriesPage")),
  "/finance/exchange-rates": lazyPage(() => import("@/features/finance/pages/ExchangeRatesPage")),
  // Yönetim defteri (finans F1, 1 Kasım 2026): Finans Özeti, Gider Defteri, Kategoriler.
  /**
   * MASRAF BEYANI (finans F2c-1, 2 Kasım 2026): «Masraflarım» çalışma alanında. Yeni beyan ve tek beyan `EXTRA_ROUTES`'ta,
   * «Masraflarım»ın kapısıyla.
   */
  "/my-expenses": lazyPage(() => import("@/features/finance/pages/MyExpensesPage")),
  "/finance": lazyPage(() => import("@/features/finance/pages/FinanceSummaryPage")),
  "/finance/ledger": lazyPage(() => import("@/features/finance/pages/FinanceLedgerPage")),
  "/finance/categories": lazyPage(() => import("@/features/finance/pages/FinanceCategoriesPage")),
  /** Finans F2c-2, 2 Kasım 2026: kontrol, beyanlar, ödeme ve zincir (`docs/plan/FINANS_MUHASEBE_PLANI.md` §7). */
  "/finance/expense-review": lazyPage(() => import("@/features/finance/pages/ExpenseReviewQueuePage")),
  "/finance/expense-claims": lazyPage(() => import("@/features/finance/pages/ExpenseClaimsPage")),
  "/finance/payables": lazyPage(() => import("@/features/finance/pages/PayablesPage")),
  "/finance/payments": lazyPage(() => import("@/features/finance/pages/PaymentsPage")),
  "/finance/settings/approval-flow": lazyPage(() => import("@/features/finance/pages/ApprovalFlowPage")),
  // Genel muhasebe (F3, 2 Kasım 2026).
  "/finance/journal": lazyPage(() => import("@/features/finance/pages/GlJournalPage")),
  "/finance/trial-balance": lazyPage(() => import("@/features/finance/pages/GlTrialBalancePage")),
  "/finance/accounts": lazyPage(() => import("@/features/finance/pages/GlAccountsPage")),
  "/finance/vat-report": lazyPage(() => import("@/features/finance/pages/GlVatReportPage")),
  "/finance/posting-rules": lazyPage(() => import("@/features/finance/pages/GlPostingRulesPage")),
  "/finance/sync": lazyPage(() => import("@/features/finance/pages/GlSyncPage")),
  "/finance/reconciliation": lazyPage(() => import("@/features/finance/pages/GlReconciliationPage")),
  "/finance/connectors": lazyPage(() => import("@/features/finance/pages/GlConnectorsPage")),
  "/finance/income-expense": lazyPage(() => import("@/features/finance/pages/FinanceIncomeExpensePage")),
  "/finance/incoming-invoices": lazyPage(() => import("@/features/finance/pages/IncomingInvoicesPage")),
  "/finance/fixed-assets": lazyPage(() => import("@/features/finance/pages/FixedAssetsPage")),
  "/finance/receivables": lazyPage(() => import("@/features/finance/pages/ReceivablesPage")),
  "/finance/bank-accounts": lazyPage(() => import("@/features/finance/pages/BankAccountsPage")),
  "/finance/bank-reconciliation": lazyPage(() => import("@/features/finance/pages/BankReconciliationPage")),
  "/finance/budgets": lazyPage(() => import("@/features/finance/pages/BudgetsPage")),
  "/finance/reports": lazyPage(() => import("@/features/finance/pages/FinanceReportsPage")),
  "/finance/periods": lazyPage(() => import("@/features/finance/pages/FinancePeriodsPage")),
  "/inventory/units": lazyPage(() => import("@/features/inventory/pages/UnitsPage")),
  "/inventory/uom-conversions": lazyPage(
    () => import("@/features/inventory/pages/UomConversionsPage"),
  ),
  "/inventory/product-categories": lazyPage(
    () => import("@/features/inventory/pages/ProductCategoriesPage"),
  ),
  "/inventory/locations": lazyPage(() => import("@/features/inventory/pages/LocationsPage")),
  "/inventory/product-barcodes": lazyPage(() => import("@/features/inventory/pages/BarcodesPage")),
  "/inventory/serials": lazyPage(() => import("@/features/inventory/pages/SerialsPage")),
  // «Kullanılabilirlik» 1 Kasım 2026'dan beri Stok'un sekmesi — rotası `EXTRA_ROUTES`'ta.

  /**
   * Platform — the vendor's own screens, reachable only on the shared service. The catalogue
   * entry carries `installation: "SHARED"`, which `gateFor` forwards, so a dedicated
   * installation answers "page not found" here whoever asks.
   */
  "/platform/tenants": lazyPage(() => import("@/features/platform/pages/TenantsPage")),
  "/platform/plans": lazyPage(() => import("@/features/platform/pages/PlansPage")),
  "/platform/subscriptions": lazyPage(() => import("@/features/platform/pages/SubscriptionsPage")),
};

/** Paths the menu cannot express: details, wizards, tabs. `under` names the gating menu item. */
/**
 * ESKİ YÖNETİM ADRESLERİ — yirmi altı tanesi, ve bu liste bir SÖZÜN yerine getirilmesi.
 *
 * Bölümler sekmeleşince bu adresler menüden düştü; rotalar ise KATALOGDAN üretildiği için
 * menüden düşen adres aynı anda rotadan da düştü. Yani "eski adresler çalışmaya devam ediyor"
 * diyen not doğru değildi: yirmi altısı da «sayfa bulunamadı» veriyordu. Ekranların kendisi
 * kaybolmamıştı — hepsi bir sekmenin içinde duruyor — ama yer imleri ve belgelere yazılmış
 * bağlantılar kırılmıştı ve bunu kimse fark etmemişti, çünkü menüde karşılığı olmayan bir
 * adresi kimse tıklamıyor, yazıyor.
 *
 * YÖNLENDİRME, kopya ekran değil: hedef sekmenin kendisi açılıyor, böylece adres çubuğu da
 * güncel adresi gösteriyor ve yer imi bir dahaki sefere doğrudan doğru yere gidiyor.
 * `replace` — geri düğmesi kullanıcıyı eski adrese geri fırlatmasın diye.
 */
export const LEGACY_ADMIN_REDIRECTS: readonly (readonly [string, string])[] = [
  ["/admin/audit-logs", "/admin/audit/logs"],
  ["/admin/cost-centers", "/admin/organization/positions"],
  ["/admin/custom-fields", "/admin/system/data-model"],
  ["/admin/delegations", "/admin/access/users"],
  ["/admin/departments", "/admin/system/modules"],
  ["/admin/field-policies", "/admin/system/data-model"],
  ["/admin/integrity", "/admin/audit/integrity"],
  ["/admin/job-titles", "/admin/organization/positions"],
  ["/admin/jobs", "/admin/system/automation"],
  ["/admin/legal-entities", "/admin/organization/legal-entities"],
  ["/admin/lookups", "/admin/system/data-model"],
  ["/admin/modules", "/admin/system/modules"],
  ["/admin/notification-templates", "/admin/system/automation"],
  ["/admin/org-units", "/admin/organization/org-units"],
  ["/admin/permission-matrix", "/admin/access/roles"],
  ["/admin/positions", "/admin/organization/positions"],
  ["/admin/roles", "/admin/access/roles"],
  ["/admin/sector-profiles", "/admin/system/modules"],
  ["/admin/security-events", "/admin/access/security"],
  ["/admin/sensitive-access", "/admin/audit/logs"],
  ["/admin/sessions", "/admin/access/security"],
  ["/admin/settings", "/admin/system/general"],
  ["/admin/sites", "/admin/organization/org-units"],
  ["/admin/storage-targets", "/admin/system/automation"],
  ["/admin/users", "/admin/access/users"],
  ["/admin/workflows", "/admin/system/automation"],
];

/**
 * ESKİ STOK ADRESLERİ — on altı tanesi.
 *
 * Yönetimde öğrenilen dersin aynısı: rotalar katalogdan üretiliyor, yani menüden düşen adres
 * aynı anda rotadan da düşer. Orada bunu fark etmeden yirmi altı adres ölmüştü ve kimse şikâyet
 * etmemişti — menüde karşılığı olmayan bir adrese kimse tıklamaz, yazar. Bu sefer önden.
 */
const LEGACY_INVENTORY_REDIRECTS: readonly (readonly [string, string])[] = [
  ["/inventory/balances", "/inventory/stock"],
  ["/inventory/reservations", "/inventory/stock"],
  ["/inventory/ledger", "/inventory/stock"],
  // `/inventory/products` and `/inventory/warehouses` are NOT listed: the sections they used
  // to redirect into now carry those very names, so the old address is the current address
  // and a redirect from a path to itself would register a second route on it.
  ["/inventory/lots", "/inventory/products"],
  ["/inventory/serials", "/inventory/products"],
  ["/inventory/documents", "/inventory/movements"],
  ["/inventory/counts", "/inventory/movements"],
  ["/inventory/reorder-rules", "/inventory/movements"],
  ["/inventory/its-notifications", "/inventory/movements"],
  ["/inventory/locations", "/inventory/warehouses"],
  ["/inventory/product-categories", "/inventory/definitions"],
  ["/inventory/product-barcodes", "/inventory/definitions"],
  ["/inventory/units", "/inventory/definitions"],
  ["/inventory/uom-conversions", "/inventory/definitions"],
];

/** ESKİ İK ADRESLERİ — on beş tanesi, sekmelerine bağlandı. */
/**
 * «Şirket» bölümü dört satırdan tek sayfaya indi (16 Eylül 2026). Eski adresler paylaşılmış
 * bağlantılarda ve yer imlerinde duruyor; dördü de kendi sekmesine gidiyor.
 */
const LEGACY_COMPANY_REDIRECTS: readonly (readonly [string, string])[] = [
  // `/company/org-chart` ile `/company/documents` zaten sekmenin kendi adresi — yönlendirme
  // gerekmiyor, eskiden de aynı yazılıyordu. Değişen yalnız şu ikisi:
  ["/partners", "/company/partners"],
  ["/my-documents", "/company/my-documents"],
];

/**
 * ESKİ KİŞİSEL İZİN ADRESLERİ — izin-sade, 20 Ekim 2026. «İzinlerim» talepleri, takvimi ve hareketleri
 * tek ekranın üç sekmesi yaptı; dört eski adres (kendi talepleri, takvim, hareketler, sağlık raporları)
 * karşılık gelen sekmeye gidiyor ve SORGUSUNU taşıyor: `?record=` (bildirim bağlantısı, onay kutusu),
 * `?panel=new&leaveType=RAPORLU` (arama paleti, Panelim), `?start=` (takvim) eskisi gibi çalışıyor.
 * Menüde zaten satırları yoktu (Panelim'den açılıyorlardı), yani bu yönlendirme yer imleri, paylaşılmış
 * bağlantılar, bildirim geçmişi ve arama paleti içindir.
 *
 * «SAĞLIK RAPORLARIM» ayrıca ele alınıyor (`legacyRedirectRoutes`): sorgusu ATILIYOR. Eski adresin
 * `?record=` değeri bir RAPOR kaydının kimliğiydi; talepler listesine taşınsa var olmayan bir talebi
 * açmaya çalışırdı. Rapor artık izin talebinin içinde.
 */
export const LEGACY_SELF_LEAVE_REDIRECTS: readonly (readonly [string, string])[] = [
  ["/leave-requests", "/my-leave/requests"],
  ["/leave-calendar", "/my-leave/calendar"],
  ["/my-leave-ledger", "/my-leave/ledger"],
  // Bölümün kökü ilk sekmeye: adres çubuğunda sekme her zaman yazılı olsun.
  ["/my-leave", "/my-leave/requests"],
];

/**
 * Organizasyon şemasının eski «Çalışanlar» bağlantısı istihdam listesinin süzgecini taşıyordu
 * (`?organizationUnitId=`); yeni tablo birimi `?unit=` ile okuyor (ik-personel 6 Ekim 2026, H5).
 */
const EMPLOYMENTS_LIST_PARAMS: readonly LegacyParamRule[] = [
  { from: "organizationUnitId", to: "unit" },
];

const LEGACY_HR_REDIRECTS: readonly (readonly [string, string, (readonly LegacyParamRule[])?])[] = [
  ["/hr/employees", "/hr/people/employees"],
  /**
   * «Kanal verisi» ayrı bir satırdı (21 Eylül 2026), sonra «Veri» sekmesi oldu; ham satır tablosu 31 Ekim
   * 2026'da kalktı (ticari veri planı §11) — her sayı artık özet ve analizden iniliyor.
   */
  ["/sales/channel-data", "/sales/channel/summary"],
  ["/sales/channel/facts", "/sales/channel/summary"],
  /** «Ecza Depoları» sekmesi kalktı (31 Ekim 2026): profil Kurallar'da, gelen rapor Mutabakat › Veri tamlığı'nda. */
  ["/sales/channel/distributors", "/sales/channel/rules"],
  /**
   * «Eşleştirme» sekmesi ne olduğunu söyleyen adına kavuştu: «Eczane Havuzu» (7 Ekim 2026).
   * Yönlendirme şart — `AdminSection` tanımadığı sekmede İLK sekmeye düşüyor, yani eski yer imi
   * hata vermeden yanlış sekmeyi (Ecza Depoları) açardı.
   */
  ["/sales/channel/mapping", "/sales/channel/pharmacy-pool"],
  /** «İstihdam Kayıtları» kalktı (H5); birim süzgeci yeni adıyla taşınıyor. */
  ["/hr/employments", "/hr/people/employees", EMPLOYMENTS_LIST_PARAMS],
  /** Girişler sekmesi kalktı (21 Ekim 2026); bekleyen girişler ve «İşe başlat» Çalışanlar'da. */
  ["/hr/people/entries", "/hr/people/employees"],
  ["/hr/org-chart", "/hr/people/org-chart"],
  ["/hr/leave-requests", "/hr/leave/requests"],
  ["/hr/leave-calendar", "/hr/leave/requests"],
  ["/hr/leave-balances", "/hr/leave/balances"],
  ["/hr/leave-ledger", "/hr/leave/balances"],
  // «İzin Türleri» 7 Ekim 2026'da «Politika ve türler» sekmesine taşındı.
  ["/hr/leave-types", "/hr/leave/policies"],
  // ["/hr/attendance", "/hr/leave/calendars"],  — puantaj kapalı
  // ik-izin 6 Ekim 2026: «Çalışma Takvimi ve Politika» sekmesi «Politika» oldu (`/hr/leave/policies`);
  // takvim ekranı kalktı, eski sekme adresi de yeni sekmeye gidiyor.
  ["/hr/work-calendars", "/hr/leave/policies"],
  ["/hr/leave-policies", "/hr/leave/policies"],
  ["/hr/leave/calendars", "/hr/leave/policies"],
  /**
   * «Özlük Dosyası» satırı kalktı — ik-personel 6 Ekim 2026 (H9). Belgeler, türler ve süresi
   * yaklaşanlar Personel › Belgeler'de; sağlık raporları İzin Yönetimi › Raporlar'da
   * (`/hr/leave/reports`, ik-izin kolu kuruyor).
   */
  ["/hr/documents", "/hr/people/documents"],
  ["/hr/medical-reports", "/hr/leave/reports"],
  ["/hr/document-types", "/hr/people/documents"],
  ["/hr/expiring", "/hr/people/documents"],
  ["/hr/personnel-files", "/hr/people/documents"],
  ["/hr/personnel-files/documents", "/hr/people/documents"],
  ["/hr/personnel-files/document-types", "/hr/people/documents"],
];

interface ExtraRoute {
  path: string;
  /**
   * Kapıyı miras alacağı menü girdisi. Menüde karşılığı OLMAYAN bir sayfa için `gate` kullanın.
   */
  under?: string;
  /**
   * Kendi kapısı: menüde bir satırı olmayan ama yine de yetki isteyen sayfalar için.
   *
   * `anyPermissions` «herhangi biri yeterli» demek ve katalogdaki kolun aynısı: bir sayfa
   * birden çok ekranı sekme olarak taşıyorsa (şirket bölümü) kapısı da onların BİRLEŞİMİDİR.
   * `permissions` hepsini isteyeceği için orada kullanılamaz.
   */
  gate?: { permissions?: readonly string[]; anyPermissions?: readonly string[]; module?: string };
  element: ComponentType;
}

/**
 * Dışa açık, YALNIZ sınama için — `SCREENS` ile aynı gerekçe. Menüden çıkan bir satırın
 * kapısının değişmediğini doğrulayan tek yer burası: satır artık katalogda olmadığı için
 * menü testleri onu göremiyor.
 */
export const EXTRA_ROUTES: readonly ExtraRoute[] = [
  /**
   * MENÜDEN ÇIKAN, AMA KAPISI DEĞİŞMEYEN EKRANLAR.
   *
   * Beşi de kenar çubuğundan kaldırıldı ve başka bir yerden açılıyor: onay ve bildirim üst
   * çubuktan, duyuru panelden, rapor yüzeyi ise şimdilik hiçbir yerden — ürün kararı.
   *
   * HEPSİ `gate` KULLANIYOR, `under` DEĞİL, ve bu ayrım bu dosyada bir kez pahalıya mal oldu:
   * «Dışa Aktarımlar» satırı bir defasında `under: "/reports"` ile bağlandı ve kapısı kendi
   * anahtarı (`system.export.run`) yerine bölümün anahtarına (`reporting.report.read`) düştü.
   * Dört rol — Depo Müdürü, Müdür, Patron, İK Müdürü — dışa aktarma yetkisi TUTMADIĞI hâlde
   * adresi yazarak ekranı açabildi. Menüden çıkan bir satırın yeri değişir, kapısı değişmez.
   *
   * `/announcements` kapısız, çünkü katalogda da kapısız satırdı: duyuru okumak herkesin işi.
   * Panelde son beşi görünüyor ve tam liste oradan açılıyor.
   */
  /*
    «EKİP GERİ BİLDİRİMLERİ» BURADAN ÇIKTI — satır menü kataloguna geri döndü (26 Ekim 2026,
    üst yönetim kararı), rota da onunla birlikte `catalogRoutes`'tan üretiliyor. Burada
    bırakılsaydı aynı adres iki kez tanımlı olurdu.
  */
  /**
   * TAKVİM — 28 Ekim 2026 kullanıcı isteği: «takvim de ayrıca takvim şekilde gözükebilsin.»
   *
   * KAPISIZ, Panelim gibi: herkesin bir takvimi var ve kendi gününü görmek bir yetki sorusu
   * değil. `?employment=` ile başkasınınki açılıyor ve kapıyı SUNUCU tutuyor (kendi / ekip /
   * şirket geneli); ekranın gizleyeceği bir şey yok, yetkisizin isteği 403 dönüyor.
   */
  {
    path: "/calendar",
    /* Kapısız ama BEYAN EDİLMİŞ: `gate: {}` — menüde satırı olmayan sayfa kapısını kendisi söyler. */
    gate: {},
    element: lazyPage(() => import("@/features/calendar/pages/CalendarPage")),
  },
  /**
   * ÜÇ KİŞİSEL EKRAN — 28 Ekim 2026 revizeleri. Kenar çubuğunda satırları YOK, girişleri
   * Panelim'deki kutucuklar: «İzinlerim» ve «Eğitimlerim» ile aynı kalıp (16 Eylül kararı —
   * kişinin kendi kaydı menüde bir bölüm değil, panelin altı).
   *
   * Kapıları KENDİ anahtarları: ekranı açan şey talebi AÇABİLMEK. Karar verenin listesi aynı
   * ekran — sunucu listeyi genişletiyor, ekran karar düğmelerini `manage`/`approve` ile
   * gösteriyor; ayrı bir «kuyruk» ekranı aynı satırları ikinci kez çizerdi.
   */
  {
    path: "/travel-requests",
    gate: { permissions: ["hr.travel_request.submit"] },
    element: lazyPage(() => import("@/features/requests/pages/TravelRequestsPage")),
  },
  {
    path: "/document-requests",
    gate: { permissions: ["hr.document_request.submit"] },
    element: lazyPage(() => import("@/features/requests/pages/DocumentRequestsPage")),
  },
  /**
   * «ZİMMETLERİM» — 30 Ekim 2026, kullanıcı: «Çalışan kendisine zimmetli eşyaları göremiyor. Kendi
   * zimmetlerini görebilsin. Bir zimmet için talep açabilsin: hasar ya da değişim.» Aynı aile: satırı yok,
   * giriş Panelim kutucuğu; kapı KENDİ anahtarı (`hr.asset_request.submit`, EMPLOYEE'de) — İK'nın
   * `hr.asset.read`'i değil, çünkü o başkasının zimmetini açıyor.
   */
  {
    path: "/my-assets",
    gate: { permissions: ["hr.asset_request.submit"], module: "module.hr" },
    element: lazyPage(() => import("@/features/asset-requests/pages/MyAssetsPage")),
  },
  {
    path: "/monthly-plans",
    gate: { permissions: ["hr.monthly_plan.write"] },
    element: lazyPage(() => import("@/features/monthly-plan/pages/MonthlyPlanPage")),
  },
  {
    path: "/approvals",
    gate: { permissions: ["workflow.approval.read"] },
    element: lazyPage(() => import("@/features/approvals/pages/ApprovalInboxPage")),
  },
  {
    path: "/notifications",
    gate: { permissions: ["notifications.notification.read"] },
    element: lazyPage(() => import("@/features/notifications/pages/NotificationsPage")),
  },
  {
    path: "/announcements",
    gate: {},
    element: lazyPage(() => import("@/features/notifications/pages/AnnouncementsPage")),
  },
  /**
   * «Puantajım» ROTASI DA KAPATILDI — 16 Eylül 2026.
   *
   * Önce yalnız menü satırı kaldırılmıştı ve burada «ekran ve kapısı duruyor» yazıyordu;
   * ölçülünce görüldü ki bu, sayfayı kapatmak değil GİZLEMEKTİ — adresi yazan giriyordu.
   * Damga kaynağı olmadığı için gördüğü şey de boş bir defterdi. Kullanıcı kararı puantajın
   * tamamının kapanması yönünde, o yüzden rota da yoruma alındı.
   *
   * `gate` ile yazılmıştı, `under` ile değil: satır geri geldiği gün kapı hiç değişmemiş
   * olacak. O biçim korunarak bırakıldı.
   */
  // {
  //   path: "/my-attendance",
  //   gate: { permissions: ["hr.attendance.read"], module: "module.hr" },
  //   element: lazyPage(() => import("@/features/attendance/pages/MyAttendancePage")),
  // },
  /**
   * KENDİ İZİN VE SAĞLIK EKRANLARI — «Benim işlerim» bölümü kalkınca buraya geldiler.
   *
   * 16 Eylül 2026, kullanıcı kararı: kenar çubuğundaki beş satırlık bölüm kaldırıldı ve dördü
   * Panelim'in altındaki «Benim işlerim» bölümüne düğme olarak indi (`MyWorkSection`). Ekranlar
   * ve adresleri aynı — yer imleri kırılmadı, paylaşılmış bağlantılar yaşıyor.
   *
   * `gate` İLE, `under` İLE DEĞİL, ve bu kez fark bir yetki boşluğu demek olurdu: Panelim
   * KAPISIZ bir satır (her oturum açan kişinin bir panosu var). `under: "/my-panel"` yazsaydık
   * dördü de kapısız kalır, yani `hr.leave.read` tutmayan biri adresi yazarak izin ekranını
   * açabilirdi. Bu dosyadaki «Dışa Aktarımlar» dersinin aynısı, daha pahalı hâli.
   *
   * KAPILAR KATALOGDAKİNİN AYNISI — satırlar silinmeden önce ne istiyorsa o: üçü
   * `hr.leave.read`, biri `hr.medical_report.read`, dördü de `module.hr`.
   *
   * KİMLİK OTURUMDAN GELİR, sunucudan çıkarsanmaz. `hr.leave.read` yalnız kaydın
   * `recordPolicy`'si SELF olduğunda sahibine daralır; İK uzmanı aynı anahtarı ANY ile taşır.
   * «İzin Taleplerim» bir zamanlar tam bu yüzden bütün şirketin iznini birinci tekil bir
   * başlığın altında listeledi. Self-servis bir yüzey kendi kapsamını SÖYLER.
   */
  /**
   * «İZİNLERİM» — izin-sade, 20 Ekim 2026. Kendi talepleri, takvimi ve hareketleri tek ekranın üç
   * sekmesi; eski `/leave-requests`, `/leave-calendar`, `/my-leave-ledger` ve `/my-medical-reports`
   * adresleri sorgularıyla buraya yönleniyor (`LEGACY_SELF_LEAVE_REDIRECTS`). Dört adres de AYNI
   * `MyLeaveSectionPage` nesnesi: sekme değişince bölüm sökülmüyor. Kapı eskisiyle aynı — `hr.leave.read`
   * ve `module.hr`; `gate`, `under` DEĞİL (Panelim kapısız bir satır). Rapor ayrı bir sayfa değil,
   * raporlu talebin içinde (`hr.medical_report.read` artık bu ekranın kapısı değil).
   */
  ...["/my-leave/requests", "/my-leave/calendar", "/my-leave/ledger"].map((path) => ({
    path,
    gate: { permissions: ["hr.leave.read"], module: "module.hr" },
    element: MyLeaveSectionPage,
  })),
  /**
   * «EĞİTİMLERİM» — ik-ek 6 Ekim 2026 (H13). Kenar çubuğunda satır YOK (16 Eylül kararı: kişinin
   * kendi ekranları Panelim'den açılıyor; giriş «Benim işlerim» → «Eğitimlerim · N bekleyen»).
   * Kapı KENDİ anahtarı: `hr.training.self` (SELF, EMPLOYEE şablonunda) — İK'nın
   * `hr.training.read`'i değil, çünkü o başkasının katılımını açıyor.
   */
  {
    path: "/my-training",
    gate: { permissions: ["hr.training.self"], module: "module.hr" },
    element: lazyPage(() => import("@/features/training/pages/MyTrainingPage")),
  },
  /**
   * «RAPORLAR» ROTASI KAPATILDI — 8 Ekim 2026, kullanılabilirlik turu.
   *
   * Satır 6 Ekim menü temizliğinde kaldırılmıştı, rota `gate` ile açık bırakılmıştı: adresi yazan
   * yine giriyordu ve gördüğü şey bir rapor değil HAM VERİYDİ — alan adları (`legal_entity_id`),
   * JSON hücreler, biçimsiz ondalıklar. «Puantajım»daki dersin aynısı: menüden kaldırmak sayfayı
   * kapatmak değil GİZLEMEKTİR. Menü kararıyla tutarlı olan, rotanın da kapanması.
   *
   * EKRAN KODU DURUYOR (`features/admin/pages/ReportsPage.tsx`), yalnız rotası yoruma alındı.
   * Rapor yüzeyi bir ürün kararıyla geri geldiği gün kapısı yine `gate` ile, kendi anahtarıyla
   * (`reporting.report.read`) gelmeli — biçim aşağıda olduğu gibi duruyor. `/reports/exports`
   * AÇIK kalıyor: «Dışa Aktarımlar» ham veri değil, kendi kapısı (`system.export.run`) olan bir iş
   * listesi ve aynı ekran Sistem › Otomasyon'da da çiziliyor; bu karar onu kapsamıyor.
   */
  // {
  //   path: "/reports",
  //   gate: { permissions: ["reporting.report.read"] },
  //   element: lazyPage(() => import("@/features/admin/pages/ReportsPage")),
  // },
  {
    path: "/reports/exports",
    gate: { permissions: ["system.export.run"] },
    element: lazyPage(() => import("@/features/admin/pages/ExportsPage")),
  },
  /*
    «SUNUM TALEPLERİM» BURADAN ÇIKTI (30 Ekim 2026) — satır menü kataloguna girdi («soldaki
    sidebarda da olsun, mümessillerde ve bölge müdüründe»), rota da onunla `catalogRoutes`'tan
    üretiliyor. Kapı aynı: `events.presentation.request` + `module.events`, katalog satırında.
  */
  /**
   * EKİP GERİ BİLDİRİMLERİ (rol-yetki 21 Ekim 2026) — yazan yönetici ve yazarın üst yönetim zincirindeki okuyucu.
   * `gate`, `under` DEĞİL: kapı KENDİ anahtarı (`hr.team_feedback.read`, EMPLOYEE'de de var — hangi kaydı kimin
   * göreceğine sunucu karar veriyor, çalışan kendisi hakkındakini ASLA göremez). Menü satırı yok: yazan Ekibim'den,
   * üst yönetim Panelim'deki bölümden açar; İK'nın satırı (`/hr/team-feedback`) İK bölümünde.
   */
  {
    path: "/team/feedback",
    gate: { permissions: ["hr.team_feedback.read"], module: "module.hr" },
    // Satır içi `lazyPage(import())`, paylaşılan nesne DEĞİL: `check-screen-access` rota metnini 400 karakterlik
    // pencerelerle okuyor ve `element: Değişken` biçimi önceki girdinin penceresine yutulup bu rotayı bir KOMŞUNUN
    // sayfasına (ürün talebi kartı) bağlıyordu — yanlış alarm. İki adres arasında geçiş senaryosu yok.
    element: lazyPage(() => import("@/features/team/pages/TeamFeedbackPage")),
  },
  /**
   * MASRAF BEYANI — yeni ve tek beyan (finans F2c-1, 2 Kasım 2026). `under: "/my-expenses"`: kapı «Masraflarım»ın
   * (`finance.expense.submit` + `module.finance`). Tek beyanı onaycı ve ekip müdürü de açabiliyor — sunucu sahibine,
   * kapsamdaki okuyucuya ve onay katılımcısına gösteriyor, ötekine 404; anahtar her çalışanda olduğu için kapı onları
   * durdurmuyor. Satır içi `lazyPage(import())` (bkz. `/team/feedback` notu: `check-screen-access` pencere okuması).
   */
  {
    path: "/my-expenses/new",
    under: "/my-expenses",
    element: lazyPage(() => import("@/features/finance/pages/ExpenseClaimPage")),
  },
  {
    path: "/my-expenses/:id",
    under: "/my-expenses",
    element: lazyPage(() => import("@/features/finance/pages/ExpenseClaimPage")),
  },
  /** TEK FİŞ VE ELLE FİŞ (F3): kapı Yevmiye Fişleri satırından; yazma düğmeleri fişin `can`'inden. */
  {
    path: "/finance/journal/new",
    under: "/finance/journal",
    element: lazyPage(() => import("@/features/finance/pages/GlEntryPage")),
  },
  {
    path: "/finance/journal/:id",
    under: "/finance/journal",
    element: lazyPage(() => import("@/features/finance/pages/GlEntryPage")),
  },
  /** HESAP EKSTRESİ (F3): kapı Hesap Planı satırından. */
  {
    path: "/finance/accounts/:id/statement",
    under: "/finance/accounts",
    element: lazyPage(() => import("@/features/finance/pages/GlStatementPage")),
  },
  /** TEK BEYANIN KONTROLÜ (F2c-2): kapı kuyruğun satırından (`finance.expense.review` + `module.finance`). */
  {
    path: "/finance/expense-review/:id",
    under: "/finance/expense-review",
    element: lazyPage(() => import("@/features/finance/pages/ExpenseReviewPage")),
  },
  /**
   * ÜRÜN TALEBİ KARTI — pz-akis 6 Ekim 2026 (plan M12). `gate`, `under` DEĞİL: kartı üç
   * masa açıyor ve üçü aynı anahtarı tutmuyor. Pazarlama listeden (`marketing.product_request
   * .read`), ruhsat ve depo onay kutusundaki görevden (`workflow.approval.read`) geliyor;
   * `under: "/product-requests"` ikincileri kendi görevlerinin kapısında durdururdu. Hangi
   * talebi kimin göreceğine sunucu karar veriyor (görev sahibi, okuyucu); kapı yalnız kapıyı
   * açıyor.
   */
  {
    path: "/product-requests/:id",
    gate: {
      anyPermissions: ["marketing.product_request.read", "workflow.approval.read"],
      module: "module.crm",
    },
    element: lazyPage(() => import("@/features/product-requests/pages/ProductRequestDetailPage")),
  },

  /** İK sekme adresleri — bölümün kapısını miras alıyorlar. */
  {
    path: "/hr/people/employees",
    under: "/hr/people",
    element: PersonnelSectionPage,
  },
  /** Analiz sekme adresleri — bölümün kapısını miras alıyorlar (A2, 1 Kasım 2026). */
  { path: "/analytics/overview", under: "/analytics", element: AnalyticsSectionPage },
  { path: "/analytics/field", under: "/analytics", element: AnalyticsSectionPage },
  { path: "/analytics/stock", under: "/analytics", element: AnalyticsSectionPage },
  { path: "/analytics/fleet", under: "/analytics", element: AnalyticsSectionPage },
  { path: "/analytics/people", under: "/analytics", element: AnalyticsSectionPage },
  { path: "/analytics/sales", under: "/analytics", element: AnalyticsSectionPage },
  { path: "/analytics/marketing", under: "/analytics", element: AnalyticsSectionPage },
  { path: "/analytics/approvals", under: "/analytics", element: AnalyticsSectionPage },
  { path: "/analytics/finance", under: "/analytics", element: AnalyticsSectionPage },
  { path: "/analytics/thresholds", under: "/analytics", element: AnalyticsSectionPage },
  { path: "/analytics/targets", under: "/analytics", element: AnalyticsSectionPage },
  /** Kanal sekme adresleri — bölümün kapısını miras alıyorlar (ticari veri, 31 Ekim 2026). */
  {
    path: "/sales/channel/summary",
    under: "/sales/channel",
    element: ChannelSectionPage,
  },
  {
    path: "/sales/channel/analysis",
    under: "/sales/channel",
    element: ChannelSectionPage,
  },
  {
    path: "/sales/channel/reconciliation",
    under: "/sales/channel",
    element: ChannelSectionPage,
  },
  {
    path: "/sales/channel/imports",
    under: "/sales/channel",
    element: ChannelSectionPage,
  },
  {
    path: "/sales/channel/rules",
    under: "/sales/channel",
    element: ChannelSectionPage,
  },
  {
    // «Eczane Havuzu» — 7 Ekim 2026'ya kadar `/sales/channel/mapping` («Eşleştirme»); eski adres
    // `LEGACY_HR_REDIRECTS`'te buraya yönleniyor.
    path: "/sales/channel/pharmacy-pool",
    under: "/sales/channel",
    element: ChannelSectionPage,
  },
  {
    path: "/hr/people/exits",
    under: "/hr/people",
    element: PersonnelSectionPage,
  },
  {
    path: "/hr/people/org-chart",
    under: "/hr/people",
    element: PersonnelSectionPage,
  },
  {
    /**
     * Personel › Belgeler — ik-personel 6 Ekim 2026 (H9). Bölümün kapısını miras alıyor
     * (`hr.employee.read`); sekmenin KENDİ kapısı (`hr.document.read`, başkası için —
     * `canForOthers`) `AdminSection`'da. Eski «Özlük Dosyası» adresleri buraya yönleniyor.
     */
    path: "/hr/people/documents",
    under: "/hr/people",
    element: PersonnelSectionPage,
  },
  {
    path: "/hr/leave/requests",
    under: "/hr/leave",
    element: lazyPage(() => import("@/features/leave/pages/LeaveSectionPage")),
  },
  {
    path: "/hr/leave/balances",
    under: "/hr/leave",
    element: lazyPage(() => import("@/features/leave/pages/LeaveSectionPage")),
  },
  {
    /**
     * ik-izin 6 Ekim 2026: `/hr/leave/calendars` idi («Çalışma Takvimi ve Politika»). Takvim
     * ekranı kalkınca sekmede yalnız politika kaldı ve adres içeriğine göre düzeltildi; eskisi
     * `LEGACY_HR_REDIRECTS`'te buraya yönleniyor.
     */
    path: "/hr/leave/policies",
    under: "/hr/leave",
    element: lazyPage(() => import("@/features/leave/pages/LeaveSectionPage")),
  },
  {
    /**
     * ik-yasam-dongusu 19 Ekim 2026 (İK-16): «Resmî tatiller» sekmesi. Bölümün kapısını
     * (`hr.leave.read`) miras alıyor; yazma eylemlerinin anahtarını (`hr.leave_config.manage`)
     * sayfa kendisi soruyor. `AdminSection` bu adresi tanımasa ilk sekmeye düşerdi.
     */
    path: "/hr/leave/holidays",
    under: "/hr/leave",
    element: lazyPage(() => import("@/features/leave/pages/LeaveSectionPage")),
  },
  {
    /**
     * ik-izin 6 Ekim 2026: «Raporlar» — sağlık raporları izin bölümünde (KARAR). Rapor bir
     * İSTİRAHAT kaydı ve izinle çakışması orada okunuyor. Bölümün kapısını (`hr.leave.read`)
     * miras alıyor; sekmenin kendi anahtarını (`hr.medical_report.read`) `AdminSection` ayrıca
     * uyguluyor.
     */
    path: "/hr/leave/reports",
    under: "/hr/leave",
    element: lazyPage(() => import("@/features/leave/pages/LeaveSectionPage")),
  },
  /**
   * STOK SEKME ADRESLERİ — bölümün kapısını miras alıyorlar; sekmenin kendi yetkisini
   * `AdminSection` ayrıca uyguluyor.
   */
  {
    /**
     * Eğitim bölümünün sekmeleri — 16 Eylül 2026'da eklendi.
     *
     * `AdminSection` sekmeyi URL'e yazıyor ama bu ikisinin rotası HİÇ YOKTU: sekmeye basmak
     * adresi değiştiriyor, yenilemek 404 veriyordu. Bölüm kökünün kapısını miras alıyorlar
     * (`under`), yani menüdeki satırla aynı yetkiyi istiyorlar.
     */
    path: "/hr/training/participation",
    under: "/hr/training",
    element: lazyPage(() => import("@/features/training/pages/TrainingSectionPage")),
  },
  {
    path: "/hr/training/courses",
    under: "/hr/training",
    element: lazyPage(() => import("@/features/training/pages/TrainingSectionPage")),
  },
  {
    path: "/inventory/stock",
    under: "/inventory",
    element: lazyPage(() => import("@/features/inventory/pages/InventorySectionPage")),
  },
  /**
   * KULLANILABİLİRLİK Stok'un SEKMESİ (1 Kasım 2026, kullanıcı: «Satış ve Depo menüsü sadeleşsin»).
   * Menü satırıydı; adres aynı kaldı, bölümün kapısını miras alıyor.
   */
  {
    path: "/inventory/availability",
    under: "/inventory",
    element: lazyPage(() => import("@/features/inventory/pages/InventorySectionPage")),
  },
  {
    path: "/inventory/products",
    under: "/inventory",
    element: lazyPage(() => import("@/features/inventory/pages/InventorySectionPage")),
  },
  {
    path: "/inventory/movements",
    under: "/inventory",
    element: lazyPage(() => import("@/features/inventory/pages/InventorySectionPage")),
  },
  {
    path: "/inventory/warehouses",
    under: "/inventory",
    element: lazyPage(() => import("@/features/inventory/pages/InventorySectionPage")),
  },
  {
    path: "/inventory/definitions",
    under: "/inventory",
    element: lazyPage(() => import("@/features/inventory/pages/InventorySectionPage")),
  },
  /**
   * STOK BELGESİ OLUŞTURMA VE TASLAK DÜZENLEME — 17 Ekim 2026 (GECE_RAPORU #6, #29). `gate`,
   * `under` DEĞİL: bölümün kapısı `inventory.balance.read` — stoğu GÖRMEK; belge açmak ayrı bir
   * yetki (`inventory.document.create`). `under: "/inventory"` yazılsaydı stok görüntüleyicisi
   * (`STOCK_VIEWER`) adresi yazarak sihirbazı açar, kaydette 403 alırdı.
   */
  {
    path: "/inventory/documents/new",
    gate: { permissions: ["inventory.document.create"], module: "module.inventory" },
    element: DocumentWizardPage,
  },
  {
    path: "/inventory/documents/:id/edit",
    gate: { permissions: ["inventory.document.create"], module: "module.inventory" },
    element: DocumentWizardPage,
  },
  /**
   * SAYIM GİRİŞİ — rafın önünde, telefonda (17 Ekim 2026). Kapısı sayı GİRME yetkisi
   * (`inventory.count.manage`); sayımı yalnız okuyan (müdür, patron) kartı Depo İşlemleri'nden açar.
   */
  {
    path: "/inventory/counts/:id/entry",
    gate: { permissions: ["inventory.count.manage"], module: "module.inventory" },
    element: lazyPage(() => import("@/features/inventory/pages/CountEntryPage")),
  },
  /**
   * «Profilim» — kenar çubuğundan çıktı, hesap menüsünde duruyor, adresi yaşıyor.
   * Kapısı katalogdan gelmiyor çünkü katalogda satırı yok; eski satırın taşıdığı yetkinin
   * aynısını burada bildiriyor.
   */
  {
    path: "/profile",
    gate: { permissions: ["directory.profile.manage"] },
    element: lazyPage(() => import("@/features/directory/pages/ProfilePage")),
  },
  /**
   * SEKME ADRESLERİ. Menü artık bölüme işaret ediyor ama bu adresler paylaşılabilir olmayı
   * sürdürüyor: bir sekme de "neye bakıyorum" sorusunun parçası. `under` ile bölümün kendi
   * kapısını miras alıyorlar — sekmenin kendi yetkisini `AdminSection` ayrıca uyguluyor, yani
   * elle yazılmış bir adres kullanıcıya yetkisi olmayan bir sekmeyi açmıyor.
   */
  /**
   * «ŞİRKET» MENÜDE BİR SATIR DEĞİL — rayın alt bandında, çıkışın üstünde duruyor
   * (`AppLayout`'un `footer` yuvası). Katalogda satırı olmadığı için kapısını kendisi
   * bildiriyor, ve kapı dört sekmenin BİRLEŞİMİ: şemayı okuyan, ortak rafı açan, cari
   * kartlara bakan ve kendi özlük evrakını gören aynı kişi olmak zorunda değil.
   *
   * Beş adres de aynı sayfayı çiziyor; hangi sekmenin açılacağını adres söylüyor. Sekmenin
   * kendi yetkisini `AdminSection` ayrıca uyguluyor.
   */
  ...[
    "/company",
    "/company/org-chart",
    "/company/documents",
    "/company/partners",
    "/company/my-documents",
  ].map((path) => ({
    path,
    gate: {
      anyPermissions: [
        "organization.org_unit.read",
        "documents.company.read",
        "partners.partner.read",
        "hr.document.read",
      ],
    },
    element: lazyPage(() => import("@/features/organization/pages/CompanySectionPage")),
  })),
  {
    path: "/admin/organization/legal-entities",
    under: "/admin/organization",
    element: lazyPage(() => import("@/features/admin/pages/OrganizationSectionPage")),
  },
  {
    path: "/admin/organization/org-units",
    under: "/admin/organization",
    element: lazyPage(() => import("@/features/admin/pages/OrganizationSectionPage")),
  },
  {
    path: "/admin/organization/document-numbers",
    under: "/admin/organization",
    element: lazyPage(() => import("@/features/admin/pages/OrganizationSectionPage")),
  },
  {
    path: "/admin/organization/positions",
    under: "/admin/organization",
    element: lazyPage(() => import("@/features/admin/pages/OrganizationSectionPage")),
  },
  {
    path: "/admin/access/users",
    under: "/admin/access",
    element: lazyPage(() => import("@/features/admin/pages/AccessSectionPage")),
  },
  {
    path: "/admin/access/roles",
    under: "/admin/access",
    element: lazyPage(() => import("@/features/admin/pages/AccessSectionPage")),
  },
  {
    path: "/admin/access/security",
    under: "/admin/access",
    element: lazyPage(() => import("@/features/admin/pages/AccessSectionPage")),
  },
  {
    path: "/admin/system/general",
    under: "/admin/system",
    element: lazyPage(() => import("@/features/admin/pages/SystemSectionPage")),
  },
  {
    path: "/admin/system/modules",
    under: "/admin/system",
    element: lazyPage(() => import("@/features/admin/pages/SystemSectionPage")),
  },
  {
    path: "/admin/system/data-model",
    under: "/admin/system",
    element: lazyPage(() => import("@/features/admin/pages/SystemSectionPage")),
  },
  {
    path: "/admin/system/automation",
    under: "/admin/system",
    element: lazyPage(() => import("@/features/admin/pages/SystemSectionPage")),
  },
  {
    /**
     * «Çalışma alanı ayarları» (KARARLAR K1 + K2): kota, limitler, saklama ve güvenlik.
     * Adres İNGİLİZCE (`workspace`), arayüz Türkçe — `route-language.test.ts` çiti.
     */
    path: "/admin/system/workspace",
    under: "/admin/system",
    element: lazyPage(() => import("@/features/admin/pages/SystemSectionPage")),
  },
  {
    /**
     * «Belge şablonları» sekmesi (`module.templates`). Kapısını `/admin/system` satırından
     * MİRAS ALIYOR ve bu doğru: sekmenin kendi kapısı (`templates.template.read`) sekme
     * listesinde duruyor ve bölüm kabuğu izni olmayana sekmeyi HİÇ çizmiyor — burada ikinci
     * kez yazmak, iki yerde tutulan tek bir kural olurdu.
     */
    path: "/admin/system/document-templates",
    under: "/admin/system",
    element: lazyPage(() => import("@/features/admin/pages/SystemSectionPage")),
  },
  {
    path: "/admin/audit/logs",
    under: "/admin/audit",
    element: lazyPage(() => import("@/features/admin/pages/AuditSectionPage")),
  },
  {
    /**
     * «Yasal saklama» sekmesi (K5). Kapısını `/admin/audit` satırından miras alıyor; sekmenin
     * KENDİ izni `system.retention.manage` ve onu `AuditSectionPage` taşıyor — burada ikinci
     * bir kapı yazmak, iki kopyanın bir gün ayrışması demek olurdu.
     */
    path: "/admin/audit/legal-holds",
    under: "/admin/audit",
    element: lazyPage(() => import("@/features/admin/pages/AuditSectionPage")),
  },
  {
    path: "/admin/audit/integrity",
    under: "/admin/audit",
    element: lazyPage(() => import("@/features/admin/pages/AuditSectionPage")),
  },
  {
    /**
     * Kişisel içerik denetimi (K4). Bölümün öbür sekmeleri gibi `under: "/admin/audit"` — kapı
     * menü satırının, sekmenin kendi anahtarı (`audit.personal_content.read`) ise
     * `AuditSectionPage` içinde: yetkisi olmayan hesapta sekme HİÇ çizilmiyor ve hiçbir istek
     * atılmıyor.
     */
    path: "/admin/audit/personal-content",
    under: "/admin/audit",
    element: lazyPage(() => import("@/features/admin/pages/AuditSectionPage")),
  },
  {
    /**
     * Etkinlik kartı. Üstü `/events` — kapısını o satırdan miras alıyor.
     *
     * ÇEKMECE DEĞİL SAYFA: bir kongrede bütçe, kalemler, katılımcılar, program ve görevler
     * var; beşini 640 piksele sığdırmak hepsini okunmaz yapardı.
     */
    path: "/events/:id",
    /**
     * `under`, `gate` DEĞİL: kapı MENÜ SATIRINDAN miras alınıyor, burada ikinci kez
     * yazılmıyor. Kapının tek yerde yaşaması, iki yerde yaşamasından iyi — iki kopya
     * ayrıldığında hangisinin doğru olduğunu kimse bilemez.
     *
     * Geçici olarak `gate` yazılmıştı ve sebebi bir tavuk-yumurtaydı: menü satırı rotayı
     * bekliyordu (`route-language` çiti «menüdeki her adres rota tablosunda vardır» diyor),
     * rota da satırı. `extraRoutes` üstünü menüde bulamayınca modül YÜKLENİRKEN fırlatıyor,
     * yani bütün rota ağacı ölüyordu — yalnız bu sayfa değil. Satır 16 Eylül 2026'da
     * eklendi, düğüm çözüldü.
     */
    under: "/events",
    element: lazyPage(() => import("@/features/events/pages/EventDetailPage")),
  },
  /** Filo: kayıt sihirbazı ve araç kartı «Araçlar» satırının kapısını, zimmet sihirbazları «Zimmet»inkini miras alıyor. */
  /**
   * SİHİRBAZLAR `gate:` ile, `under:` DEĞİL. `under` listenin OKUMA kapısını miras verir: araç
   * okuyabilen ama yazamayan biri adresi yazınca kayıt/teslim/iade sihirbazını açar, beş adım
   * doldurur ve son düğmede 403 alırdı. Kapı, sayfanın sonunda çağırdığı ucun anahtarı.
   */
  {
    path: "/fleet/vehicles/new",
    gate: { permissions: ["fleet.vehicle.manage"], module: "module.fleet" },
    element: lazyPage(() => import("@/features/fleet/pages/FleetVehicleNewPage")),
  },
  /**
   * «Vadeler» (10 Ekim 2026; plan §3.2 «Araçlar › Liste · Vadeler»). Ayrı menü satırı DEĞİL — filo
   * paneli altı satırda, kitin sınırı. «Araçlar» satırının okuma kapısını miras alır (`under`);
   * statik yol `:id`'den önce eşleşir.
   */
  { path: "/fleet/vehicles/due", under: "/fleet/vehicles", element: lazyPage(() => import("@/features/fleet/pages/FleetDuePage")) },
  { path: "/fleet/vehicles/:id", under: "/fleet/vehicles", element: lazyPage(() => import("@/features/fleet/pages/FleetVehicleDetailPage")) },
  {
    path: "/fleet/assignments/new",
    gate: { permissions: ["fleet.assignment.manage"], module: "module.fleet" },
    element: lazyPage(() => import("@/features/fleet/pages/FleetHandoverPage")),
  },
  /** Zimmetin tam sayfası (30 Ekim 2026): hasar, ceza, HGS, yakıt, konum, belgeler — çekmeceden açılır. */
  { path: "/fleet/assignments/:id", under: "/fleet/assignments", element: lazyPage(() => import("@/features/fleet/pages/FleetAssignmentDetailPage")) },
  {
    path: "/fleet/assignments/:id/return",
    gate: { permissions: ["fleet.assignment.manage"], module: "module.fleet" },
    element: lazyPage(() => import("@/features/fleet/pages/FleetReturnPage")),
  },
  {
    path: "/fleet/imports/new",
    gate: { anyPermissions: ["fleet.fine.manage", "fleet.cost.manage", "fleet.vehicle.manage"], module: "module.fleet" },
    element: lazyPage(() => import("@/features/fleet/pages/FleetImportPage")),
  },
  {
    path: "/fleet/integrations",
    gate: { permissions: ["fleet.integration.read"], module: "module.fleet" },
    element: lazyPage(() => import("@/features/fleet/pages/FleetIntegrationsPage")),
  },
  {
    /**
     * pz-gorev 6 Ekim 2026: paylaşılan stant ziyaret listesi (M8) — bildirimin bağlantısı.
     *
     * `gate`, `under` DEĞİL ve bilerek: okuyan çoğu zaman `events.event.read` taşımayan,
     * listenin paylaşıldığı kişi. `/events` satırının kapısını miras alsaydı bağlantı ona hiç
     * açılmazdı. Kapı yalnız modül; veriyi sunucu veriyor — katılımcı anahtarı sahibine ve
     * paylaşılan kişiye, ötekilere 404 ve ekran «Sayfa bulunamadı».
     */
    path: "/events/:id/stand-visits",
    gate: { module: "module.events" },
    element: lazyPage(() => import("@/features/events/pages/StandVisitsSharedPage")),
  },
  {
    /**
     * pz-gorev 6 Ekim 2026: görevin kendi adresi (M7) — görev bildirimlerinin bağlantısı.
     *
     * Kongreyi okuyabileni kongre kartına (çekmece açık) yönlendiriyor; kongreyi okuyamayan
     * ATANAN görevini burada görüyor. Kapı yok (`gate: {}`): görev çekirdek ve kimin
     * göreceğini sunucu söylüyor, göremeyene 404.
     */
    path: "/tasks/:id",
    gate: {},
    element: lazyPage(() => import("@/features/events/pages/TaskPage")),
  },
  {
    path: "/hr/employees/:id",
    /**
     * ÜST BAĞI «Personel» BÖLÜMÜ, artık menüde olmayan `/hr/employees` değil.
     *
     * Çalışan kartı kendi adresini korudu — yer imi ve belgeye yazılmış bağlantılar kırılmasın
     * diye — ama kapısını miras aldığı satır sekmeye taşındı. `extraRoutes` üstü bulamayınca
     * modül yüklenirken fırlatıyor ve bunu `router.test.ts` ilk koşuda yakaladı; sessizce
     * çalışmaya devam edip yalnız o sayfaya girildiğinde patlayan bir hata olmadı.
     */
    under: "/hr/people",
    element: lazyPage(() => import("@/features/employees/pages/EmployeeCardPage")),
  },
  /**
   * Workspace detail paths. Each inherits its list item's gate, so a conversation, a note or an
   * editor session is never reachable by a URL the menu itself would not offer.
   */
  {
    path: "/chat/:conversationId",
    under: "/chat",
    element: lazyPage(() => import("@/features/chat/pages/ChatPage")),
  },
  {
    /**
     * `/notes` ile AYNI tembel nesne (`NotesWorkspacePage`): ray'dan bir sayfa seçmek ekranı
     * yeniden kurmuyor — ray (açık klasörler, kaydırma) yerinde kalıyor, yalnız sağdaki belge
     * değişiyor. İki ayrı `lazy()` çağrısı iki ayrı bileşen tipi olurdu ve React her geçişte
     * rayı söküp yeniden takardı.
     */
    path: "/notes/:noteId",
    under: "/notes",
    element: NotesWorkspacePage,
  },
  {
    path: "/diagrams/:diagramId",
    under: "/diagrams",
    element: lazyPage(() => import("@/features/diagrams/pages/DiagramEditorPage")),
  },
  {
    path: "/documents/:sessionId",
    under: "/documents",
    element: lazyPage(() => import("@/features/documents/pages/DocumentEditorPage")),
  },
  {
    /**
     * ŞABLONDAN DOLDURULAN BELGE — `gate:`, `under:` DEĞİL.
     *
     * Ekran Dosyalarım'dan açılıyor ve oraya dönüyor, yani `under: "/documents"` yazmak doğal
     * görünüyor. Yanlış olurdu: `under` EBEVEYNİN kapısını miras verir, ve bu ekranın kapısı
     * `templates.document.read` — Dosyalarım'ınki değil. Miras alsaydı, şablon anahtarı
     * OLMAYAN ama Dosyalarım'ı görebilen herkes adresi yazarak bu ekranı açardı.
     *
     * Bu dosyanın «Dışa Aktarımlar» dersi tam olarak buydu: menüden çıkan bir satırın YERİ
     * değişir, KAPISI değişmez.
     */
    path: "/template-documents/:documentId",
    gate: { permissions: ["templates.document.read"] },
    element: lazyPage(() => import("@/features/templates/pages/TemplateDocumentPage")),
  },
];

function gateFor(
  item: {
    permissions?: readonly string[];
    anyPermissions?: readonly string[];
    module?: string;
    panel: NavPanel;
    aboutOthers: boolean;
    installation?: Installation;
  },
  element: ReactNode,
) {
  return (
    <ProtectedRoute
      {...(item.permissions ? { all: item.permissions } : {})}
      {...(item.anyPermissions ? { anyOf: item.anyPermissions } : {})}
      {...(item.module ? { module: item.module } : {})}
      {...(item.installation ? { installation: item.installation } : {})}
      // The same panel rule the menu filters by, so a typed URL cannot outrank the menu.
      requireAnyRecord={item.aboutOthers}
    >
      <RouteBoundary>{element}</RouteBoundary>
    </ProtectedRoute>
  );
}

function catalogRoutes(): RouteObject[] {
  return NAV_ITEMS.map((item) => {
    const Page = SCREENS[item.to] ?? PlannedScreenPage;
    return {
      path: item.to,
      element: gateFor(item, <Page />),
    } satisfies RouteObject;
  });
}

function legacyRedirectRoutes(): RouteObject[] {
  return [
    ...LEGACY_ADMIN_REDIRECTS,
    ...LEGACY_INVENTORY_REDIRECTS,
    ...LEGACY_HR_REDIRECTS,
    ...LEGACY_COMPANY_REDIRECTS,
    ...LEGACY_SELF_LEAVE_REDIRECTS,
  ]
    .map(([from, to, rules]: readonly [string, string, (readonly LegacyParamRule[])?]) => ({
      path: from,
      // Kapı YOK: hedef sekme kendi kapısını zaten uyguluyor ve yönlendirmeyi ayrıca korumak,
      // aynı kuralı iki yerde tutmak olurdu. Yetkisi olmayan kişi hedefte durdurulur.
      // Sorgu ve çapa TAŞINIYOR, adı değişen anahtar çevriliyor (ik-personel + ik-izin 6 Ekim 2026;
      // `LegacyRedirect`'in açıklaması).
      element: <LegacyRedirect to={to} rules={rules} />,
    }))
    .concat([
      {
        // «Sağlık raporlarım» (izin-sade, 20 Ekim 2026): rapor artık izin talebinin içinde. SORGU
        // TAŞINMIYOR — eski `?record=` bir rapor kaydıydı, bir talep kimliği değil.
        path: "/my-medical-reports",
        element: <Navigate to="/my-leave/requests" replace />,
      },
    ]);
}

function extraRoutes(): RouteObject[] {
  return EXTRA_ROUTES.map(({ path, under, gate, element: Page }) => {
    /**
     * Menüde satırı olmayan sayfa da KAPISIZ olamaz.
     *
     * «Profilim» menüden çıkarıldı — hesap menüsünde zaten var — ama adresi duruyor ve elle
     * yazılabiliyor. `under` verilemediği için kapısını kendisi bildiriyor; kapıyı büsbütün
     * atlamak, menüden çıkarmayı bir yetki boşluğuna çevirirdi.
     */
    if (gate) {
      const item = {
        ...(gate.permissions ? { permissions: gate.permissions } : {}),
        ...(gate.anyPermissions ? { anyPermissions: gate.anyPermissions } : {}),
        ...(gate.module ? { module: gate.module } : {}),
        panel: "workspace" as NavPanel,
        aboutOthers: false,
      };
      return { path, element: gateFor(item, <Page />) } satisfies RouteObject;
    }
    const parent = NAV_ITEMS.find((item) => item.to === under);
    if (!parent) throw new Error(`Rota kataloğa bağlı değil: ${path} → ${under ?? "?"}`);
    return { path, element: gateFor(parent, <Page />) } satisfies RouteObject;
  });
}

export const router = createBrowserRouter([
  {
    path: "/login",
    element: (
      <RouteBoundary>
        <LoginPage />
      </RouteBoundary>
    ),
    // Kabuğun dışındaki üç rotanın da hata sınırı var (9 Ekim 2026, 5. tur) — `publicRouteError`.
    errorElement: publicRouteError,
  },
  {
    /**
     * Parola sıfırlama. Adres BİR SÖZLEŞME: sunucu onu sıfırlama postasının içine yazıyor
     * (`passwordResetMail`, arka uçtaki `src/infra/mail/templates.ts`) ve giriş ekranındaki
     * «Parolamı unuttum» bağlantısı da buraya geliyor. Uzun süre bu rota YOKTU — iki uç ve
     * iki bağlantı vardı, sayfa yoktu; parolasını unutan herkes 404 görüyordu (16 Eylül 2026,
     * `route-language.test.ts` kod içindeki bağlantıları taramaya başlayınca bulundu).
     *
     * Kabuğun dışında, `/login` ve `/invite` gibi: buraya gelen kişinin oturumu yok.
     */
    path: "/reset-password",
    element: (
      <RouteBoundary>
        <ResetPasswordPage />
      </RouteBoundary>
    ),
    errorElement: publicRouteError,
  },
  {
    /**
     * The invitation link. `/invite?token=…` is what the backend writes into the invitation
     * mail (`invitationMail` in the backend's `src/infra/mail/templates.ts`), so the path is a
     * contract with every message already sent, not a naming choice. Outside the shell like
     * `/login`: the visitor has an account but no password yet, so no session can exist.
     */
    path: "/invite",
    element: (
      <RouteBoundary>
        <AcceptInvitePage />
      </RouteBoundary>
    ),
    errorElement: publicRouteError,
  },
  {
    /**
     * The component gallery — OUTSIDE the shell, and deliberately so.
     *
     * It is a catalogue of components rendered against fixed sample data: it reads no session,
     * no company and no API. Putting it inside the authenticated layout meant it demanded a
     * login it had no use for, and rendered a blank page without one. It is also the surface
     * somebody opens to check a colour or a spacing while signed out of a different
     * environment, so requiring a session made it useless for its actual purpose.
     *
     * Its own route also keeps it out of the navigation catalogue, which is what stops it
     * appearing in anybody's menu.
     */
    path: "/dev/style-guide",
    element: (
      <RouteBoundary>
        {StyleGuidePage ? <StyleGuidePage /> : <Navigate to="/" replace />}
      </RouteBoundary>
    ),
  },
  {
    element: <AppLayout />,
    /**
     * Kabuğun KENDİSİ düşerse (oturum, ray, üst çubuk) tam sayfa hata — başka çare yok.
     */
    errorElement: (
      <RouteBoundary>
        <RouteErrorPage />
      </RouteBoundary>
    ),
    children: [
      {
        /**
         * EKRANIN HATASI KABUĞUN İÇİNDE (9 Ekim 2026, kabuk turu). Hata sınırı yalnız yukarıdaki
         * kabuk rotasındaydı; React Router hatayı en yakın `errorElement`'e taşıyıp ONU, o rotanın
         * öğesinin YERİNE çiziyor — yani düşen tek bir ekran rayı, üst çubuğu, şirket seçiciyi
         * ve sohbeti de götürüyordu. `RouteErrorPage`'in kendi sözü («kabuk çalışmaya devam
         * eder, kişi başka yere gidebilir») ve `errors-and-monitoring.md` («a failing widget
         * never blanks the shell») tutulmuyordu. Yolsuz bu ara rota hatayı kabuğun `<Outlet/>`'ine
         * çiziyor: menü yerinde, kişi başka ekrana geçebiliyor. Öğesi yok, yani varsayılanı
         * `<Outlet/>` — ekranlar için hiçbir şey değişmiyor.
         */
        errorElement: (
          <RouteBoundary>
            <RouteErrorPage />
          </RouteBoundary>
        ),
        children: [
          /**
           * The landing page is `/my-panel` for everybody, including HR staff. An HR user is an
           * employee first — their own leave and payslips are theirs before their colleagues' are
           * their work — and the panel switcher is one click away.
           */
          /**
           * SON ÇARE, ilk tercih değil.
           *
           * Kökü `AppLayout` karşılıyor ve kişiyi KENDİ panosuna yolluyor — sistem yöneticisini
           * «Panelim»e düşürmek, personel kaydı olmayan bir hesaba boş bir izin bakiyesi
           * göstermek demekti. Bu satır yalnız o hesabın rayında hiç pano yoksa çalışıyor.
           *
           * `/` yazılamaz: kökü kökün kendisine yönlendirmek sonsuz döngü olurdu — `AppLayout`'un
           * yönlendirmesi ancak bir pano bulduğunda devreye giriyor ve bulamadığında akış TAM DA
           * buraya düşüyor.
           */
          { index: true, element: <Navigate to="/my-panel" replace /> },
          ...catalogRoutes(),
          ...extraRoutes(),
          /**
           * Eski adresler EN SONDA: gerçek bir rota varsa o kazanır, yönlendirme yalnız artık
           * kimsenin sahiplenmediği adresi yakalar.
           */
          ...legacyRedirectRoutes(),
          {
            path: "*",
            element: (
              <RouteBoundary>
                <NotFoundPage />
              </RouteBoundary>
            ),
          },
        ],
      },
    ],
  },
]);
